import * as React from 'react';
import type {
  LinkData,
  MediaPlanPreviewData,
  MediaPlanWriteResult
} from 'shared/src/api/ai-media-plan-chat-types';
import type { Platform } from 'shared/src/domain/line-item/line-item-channels';
import {
  BEESWAX_MEDIA_PLATFORM_ID,
  FACEBOOK_MEDIA_PLATFORM_ID,
  GOOGLE_SEARCH_MEDIA_PLATFORM_ID
} from 'shared/src/domain/media-platform/media-platforms';
import type { CommitOutcome } from '@/features/ai/shared/commit-card-frame';
import { BeeswaxMediaPlanPreviewCard } from './beeswax/beeswax-media-plan-preview-card';
import { GoogleAdsMediaPlanPreviewCard } from './google-ads/google-ads-media-plan-preview-card';
import { MetaMediaPlanPreviewCard } from './meta/meta-media-plan-preview-card';

export type MediaPlanPreviewCardProps = {
  data: MediaPlanPreviewData;
  strategyId: string;
  lineItemName: string;
  onFieldsEdited?: (summary: string) => void;
  onCreated?: () => void;
  onCreatedLinks?: (links: LinkData[]) => void;
  nextPlatform?: Platform;
  onRequestNewPlatform?: (platformId: number) => void;
  // Confirmed-write transport (Beeswax skill path). When `toolCallId` is set, the card calls the
  // beeswaxConfirmMediaPlan endpoint and resolves the tool via `onResolve` on every terminal
  // outcome (created / cancelled / failed) instead of the legacy confirmMediaPlanCreate + onCreated.
  toolCallId?: string;
  /** Outcome of a resolution made outside the card (prior session, or the modal's soft-cancel). */
  externalOutcome?: CommitOutcome | null;
  /** The preview content changed after the card resolved (bare re-propose) — reopen the receipt. */
  updatedAfterResolve?: boolean;
  onResolve?: (output: MediaPlanWriteResult) => void | Promise<void>;
};

export type MediaPlanPreviewCardComponent = (props: MediaPlanPreviewCardProps) => React.JSX.Element;

const PREVIEW_CARDS: Record<number, MediaPlanPreviewCardComponent> = {
  [BEESWAX_MEDIA_PLATFORM_ID]: BeeswaxMediaPlanPreviewCard,
  [GOOGLE_SEARCH_MEDIA_PLATFORM_ID]: GoogleAdsMediaPlanPreviewCard,
  [FACEBOOK_MEDIA_PLATFORM_ID]: MetaMediaPlanPreviewCard
};

export function MediaPlanPreviewCard(props: MediaPlanPreviewCardProps): React.JSX.Element {
  const Component = PREVIEW_CARDS[props.data.platform_id];
  if (!Component) {
    throw new Error(
      `No media plan preview card registered for platform_id=${props.data.platform_id}. ` +
        `Register a card for this platform before using Assist Me on it.`
    );
  }
  return <Component {...props} />;
}
